import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { toast } from "sonner";

export function InvoicingTab() {
	const { data: settings, isLoading: settingsLoading } = trpc.qbo.getInvoiceSettings.useQuery();
	const { data: items, isLoading: itemsLoading } = trpc.qbo.listItems.useQuery();
	const updateSettings = trpc.qbo.updateInvoiceSettings.useMutation();
	const utils = trpc.useUtils();
	const [saving, setSaving] = useState(false);

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSaving(true);
		try {
			const form = new FormData(e.currentTarget);
			const selectedItemId = (form.get("defaultItemId") as string) || undefined;
			const selectedItem = items?.find((i) => i.id === selectedItemId);

			await updateSettings.mutateAsync({
				defaultItemId: selectedItemId,
				defaultItemName: selectedItem?.name,
				dueDateTerms:
					(form.get("dueDateTerms") as "on_receipt" | "net_15" | "net_30" | "net_60") || undefined,
				autoSendEmail: form.get("autoSendEmail") === "on",
				customerMemoTemplate: (form.get("customerMemoTemplate") as string) || undefined,
			});
			await utils.qbo.getInvoiceSettings.invalidate();
			toast.success("Invoice settings saved");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to save settings");
		} finally {
			setSaving(false);
		}
	}

	if (settingsLoading) {
		return (
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<div className="h-8 w-48 animate-pulse rounded bg-[#f3f4f6]" />
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white">
			<div className="border-b border-[#e5e7eb] px-6 py-4">
				<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
					Invoice Push Settings
				</h2>
				<p className="mt-1 text-xs text-[#9ca3af]">
					Configure how invoices are created in QuickBooks Online.
				</p>
			</div>
			<form onSubmit={handleSubmit} className="space-y-4 p-6">
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-1">
						<label htmlFor="defaultItemId" className="text-xs font-medium text-[#1e1e20]">
							Default Service Item
						</label>
						<select
							id="defaultItemId"
							name="defaultItemId"
							defaultValue={settings?.defaultItemId ?? ""}
							disabled={itemsLoading}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 disabled:opacity-50"
						>
							<option value="">-- Select item --</option>
							{items?.map((item) => (
								<option key={item.id} value={item.id}>
									{item.name} ({item.type})
								</option>
							))}
						</select>
						<p className="text-xs text-[#9ca3af]">QBO item used for all invoice line items.</p>
					</div>

					<div className="space-y-1">
						<label htmlFor="dueDateTerms" className="text-xs font-medium text-[#1e1e20]">
							Payment Terms
						</label>
						<select
							id="dueDateTerms"
							name="dueDateTerms"
							defaultValue={settings?.dueDateTerms ?? "on_receipt"}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						>
							<option value="on_receipt">Due on Receipt</option>
							<option value="net_15">Net 15</option>
							<option value="net_30">Net 30</option>
							<option value="net_60">Net 60</option>
						</select>
					</div>
				</div>

				<div className="space-y-1">
					<label htmlFor="customerMemoTemplate" className="text-xs font-medium text-[#1e1e20]">
						Customer Memo Template
					</label>
					<input
						id="customerMemoTemplate"
						name="customerMemoTemplate"
						type="text"
						defaultValue={settings?.customerMemoTemplate ?? ""}
						placeholder="e.g. Payroll processing for {periodStart} - {periodEnd}"
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
					<p className="text-xs text-[#9ca3af]">
						Available variables: {"{customerName}"}, {"{periodStart}"}, {"{periodEnd}"}
					</p>
				</div>

				<div className="flex items-center gap-2">
					<input
						type="checkbox"
						id="autoSendEmail"
						name="autoSendEmail"
						defaultChecked={settings?.autoSendEmail ?? false}
						className="h-4 w-4 rounded border-[#d1d5db] text-[#16a34a] focus:ring-[#4f46e5]"
					/>
					<label htmlFor="autoSendEmail" className="text-sm font-medium text-[#1e1e20]">
						Auto-send invoice email when pushed to QBO
					</label>
				</div>

				<button
					type="submit"
					disabled={saving}
					className="rounded bg-[#4f46e5] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
				>
					{saving ? "Saving..." : "Save Settings"}
				</button>
			</form>
		</div>
	);
}
